import React, { useEffect, useState } from 'react';
import logo from './logo.svg';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import WordRecord from './pages/WordRecord';
import WordDetail from './pages/WordDetail';
import WordEdit from './pages/WordEdit';
import WordDictionary from './pages/WordDictionary';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ChildRegister from './pages/ChildRegister';
import SignupComplete from './pages/SignupComplete';
import CardWordRecord from './pages/CardWordRecord';
import ConversationRecord from './pages/ConversationRecord';
import Home from './pages/Home'
import MyPage from './pages/MyPage';
import ConversationNotes from './pages/ConversationNotes';
import ConversationTranscript from './pages/ConversationTranscript';
import TermsType from './pages/TermsType';
import ResetPassword from './pages/ResetPassword';
import VoiceRegisterIntro from './pages/VoiceRegisterIntro';
import VoiceRegister from './pages/VoiceRegister';
import Report from './pages/Report';
import TutorialList from './pages/TutorialList';
import TutorialDetail from './pages/TutorialDetail';
import ChannelService from './ChannelService';
import { getLoginStatus } from './api/auth';
import { getUserInfo } from './api/user';
import RegistrationGuard from './components/molecules/auth/RegistrationGuard';

function ChannelTalkManager() {
  const location = useLocation();
  const [bootedStatus, setBootedStatus] = useState('none');

  useEffect(() => {
    ChannelService.loadScript();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const isSignupPath = path.startsWith('/signup');
    const isTutorialPath = path.startsWith('/mypage/tutorials/');
    const isLoginPath = path.startsWith('/login');
    const isResetPasswordPath = path.startsWith('/reset-password');

 const manageChannelTalk = async () => {
      // 1. 위치는 최상단에서 호출
      ChannelService.setPage(window.location.href);
      ChannelService.track('UpdatePage', { url: window.location.href });

      // 2. 회원가입/비밀번호 재설정 페이지에서는 shutdown
      if (isSignupPath || isResetPasswordPath) {
        ChannelService.shutdown();
        setBootedStatus('none'); 
        return;
      }

      // 3. 로그인 페이지 처리 (세션 전환)
      if (isLoginPath) {
        if (bootedStatus !== 'guest') {
          ChannelService.boot({ pluginKey: process.env.REACT_APP_CHANNEL_TALK_PLUGIN_KEY });
          setBootedStatus('guest');
          ChannelService.setPage(window.location.href);
        }
      } 
      // 4. 일반/튜토리얼 페이지 인증 로직 (이미 member라면 건너뜀)
      else if (bootedStatus !== 'member' && !isSignupPath && !isResetPasswordPath) {
        try {
          const userInfo = await getUserInfo();
          const memberId = userInfo?.userId || null;

          if (memberId) {
            ChannelService.shutdown();
            ChannelService.boot({
              pluginKey: process.env.REACT_APP_CHANNEL_TALK_PLUGIN_KEY,
              memberId: String(memberId),
              profile: { 
                name: userInfo.userName, 
                email: userInfo.email,
                mobileNumber: userInfo.phoneNumber 
              },
              keepMessengerState: false
            });
            setBootedStatus('member');
            const currentPath = window.location.origin + location.pathname;
            ChannelService.setPage(currentPath);
            // ChannelService.setPage(window.location.href);
          } else if (bootedStatus !== 'guest') {
            ChannelService.boot({ pluginKey: process.env.REACT_APP_CHANNEL_TALK_PLUGIN_KEY });
            setBootedStatus('guest');
          }
        } catch (error) {
          if (bootedStatus !== 'guest') {
            ChannelService.boot({ pluginKey: process.env.REACT_APP_CHANNEL_TALK_PLUGIN_KEY });
            setBootedStatus('guest');
          }
        }
      }

      // 5. 튜토리얼 페이지에서는 hide
      if (isTutorialPath) {
        ChannelService.hideChannelButton(); // 버튼 숨기기
        ChannelService.hideMessenger(); // 창 닫기
      } else if (!isSignupPath && !isResetPasswordPath) {
        // 회원가입/튜토리얼이 아닌 모든 페이지에서는 버튼 보이기
        ChannelService.showChannelButton(); 
      }

      ChannelService.setPage(window.location.href);
    };

    manageChannelTalk();
  }, [location.pathname]);

  return null;
}

// signup 경로 벗어날 시 sessionStorage 초기화
function ClearSignupSession() {
  const location = useLocation();
  useEffect(() => {
    const isSignupPath = location.pathname.startsWith('/signup');
    if (!isSignupPath) {
      sessionStorage.removeItem('emailVerified');
      sessionStorage.removeItem('termsAgreed');
      sessionStorage.removeItem('terms');
      sessionStorage.removeItem('userInfo');
    }
  }, [location.pathname]);
  return null;
}

// 가드 컴포넌트
const PublicRoute = ({ isLoggedIn, children }) => {
  if (isLoggedIn) {
    // 로그인 상태면 홈으로 이동
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const res = await getLoginStatus();
        setIsLoggedIn(res); 
      } catch {
        setIsLoggedIn(false);
      } finally {
        setAuthLoading(false);
      }
    };
    checkLoginStatus();
  }, []);

  if (authLoading) return null;

  return (
    <div className="App">
       <BrowserRouter>
        <ChannelTalkManager />
        <ClearSignupSession/>
        <Routes>
          {/* 로그인한 유저는 로그인 및 회원가입 페이지에 접근할 수 없음 */}
          <Route path="/login" element={
            <PublicRoute isLoggedIn={isLoggedIn}><Login /></PublicRoute>
          } />
          {/* <Route path="/login" element={<Login/>}/> */}
          <Route path="/reset-password" element={
            <PublicRoute isLoggedIn={isLoggedIn}><ResetPassword/></PublicRoute>
          } />
          <Route path="/signup/:step" element={
            <PublicRoute isLoggedIn={isLoggedIn}><Signup/></PublicRoute>
          } />
          <Route path="/signup/terms/:type" element={
              <PublicRoute isLoggedIn={isLoggedIn}><TermsType/></PublicRoute>
          } />
          <Route path="/signup/child-register" element={
              <PublicRoute isLoggedIn={isLoggedIn}><ChildRegister next="/signup/voice-register"/></PublicRoute>
          } />
          <Route path="/signup/voice-register" element={
              <PublicRoute isLoggedIn={isLoggedIn}><VoiceRegisterIntro/></PublicRoute>
          } />
          <Route path="/signup/voice-register/record" element={
              <PublicRoute isLoggedIn={isLoggedIn}><VoiceRegister next="/signup/complete"/></PublicRoute>
          } />
          <Route path="/signup/complete" element={
              <PublicRoute isLoggedIn={isLoggedIn}><SignupComplete/></PublicRoute>
          } />
          
          <Route path="/conversation-word" element={<ConversationRecord/>}/>
          <Route path="/card-word" element={<CardWordRecord/>}/>
          <Route path="/words/new" element={<WordRecord/>}/>
          
          <Route path="/words/:id" element={<WordDetail/>}/>
          <Route path="/words/:id/edit" element={<WordEdit/>}/>
          <Route path="/words" element={<WordDictionary/>}/>
          <Route path="/conversations" element={<ConversationNotes/>}/>
          <Route path="/conversations/:id" element={<ConversationTranscript/>}/>
          <Route path="/" element={<Home/>}/>
          <Route path="/mypage" element={<MyPage/>}/>
          <Route path="/mypage/child-register" element={<ChildRegister next="/mypage"/>}/>
          <Route 
            path="/mypage/voice-register" 
            element={
              <VoiceRegister 
                next="/mypage" 
                onComplete={() => alert("음성 등록이 완료되었어요")}
              />
            }
          />
          <Route path="/mypage/tutorials" element={<TutorialList/>}/>
          <Route path="/mypage/tutorials/:type" element={<TutorialDetail/>}/>
          <Route path="/report" element={<Report/>}/>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
